import { ICellRendererParams } from 'ag-grid-community';
import { MetricCategories } from '@aleph/shared-components';
import { UserAccountLevel, UserCredential, UserMetaDataQuery } from 'wb-reddit/graphql/generated';
import { User } from 'wb-reddit/app/routes/teams/utils';
import { AuthGraphUserMetaDataQuery, UserAccountLevel as AuthGraphUserAccountLevel } from 'wb-reddit/graphql/authgraph/generated';

type AccountLevel = UserAccountLevel | AuthGraphUserAccountLevel;

export const userHasOneOfRoles = (
	user: UserMetaDataQuery['me'] | AuthGraphUserMetaDataQuery['me'] | undefined | User,
	roles: UserAccountLevel[] | AuthGraphUserAccountLevel[],
	// @ts-ignore TODO fix
) => roles.includes(user?.userAccountLevel as AccountLevel);

export const userHasOneOfCredentials = (
	user: UserMetaDataQuery['me'] | undefined,
	credentials: Partial<UserCredential>[],
) => credentials.some(
	credential => (user?.userCredential ?? []).some(
		userCredential => credential.credentialName === userCredential.credentialName && (
			credential.userCredentialRead === userCredential.userCredentialRead
			|| credential.userCredentialWrite === userCredential.userCredentialWrite
			|| credential.userCredentialDelete === userCredential.userCredentialDelete
		),
	),
);

export const getExtraCreativeContent = (params: ICellRendererParams) => (new Array(6)).fill(null).map((_, idx) => ({
	creativeContentId:             params?.data?.[`creativeContentId${idx}`],
	creativeContentMediaUrl:       params?.data?.[`creativeContentMediaUrl${idx}`]?.mediaUrl,
	creativeContentCallToAction:   params?.data?.[`creativeContentCallToAction${idx}`],
	creativeContentCaption:        params?.data?.[`creativeContentCaption${idx}`],
	creativeContentDestinationUrl: params?.data?.[`creativeContentDestinationUrl${idx}`],
	creativeContentDisplayUrl:     params?.data?.[`creativeContentDisplayUrl${idx}`],
}));

// TODO translate
export const REDDIT_CATEGORIZED_COLUMNS: MetricCategories = [
	{
		value:   'general',
		label:   'General performance',
		metrics: [
			{ value: 'impressions', label: 'Impressions' },
			{ value: 'clicks', label: 'Clicks' },
			{ value: 'spend', label: 'Spend' },
			{ value: 'viewerComments', label: 'Comments' },
			{ value: 'ecpm', label: 'eCPM (Effective cost per mille)' },
			{ value: 'cpc', label: 'CPC (Cost per click)' },
			{ value: 'ctr', label: 'CTR (Click-through rate)' },
		],
	},
	{
		value:   'appInstalls',
		label:   'App Installs',
		metrics: [
			{ value: 'appInstallMetricsInstall', label: 'App Installs' },
			{ value: 'appInstallMetricsSpend', label: 'App Installs Spend' },
			{ value: 'appInstallMetricsAddToCart', label: 'App Add To Cart' },
			{ value: 'appInstallMetricsSignUp', label: 'App Sign Up' },
			{ value: 'appInstallMetricsLevelAchieved', label: 'App Level Achieved' },
			{ value: 'appInstallMetricsSpendCredits', label: 'App Spend Credits' },
			{ value: 'appInstallMetricsPurchase', label: 'App Purchases' },
			{ value: 'appInstallMetricsAddPaymentInfo', label: 'App Added Payment Info' },
			{ value: 'appInstallMetricsSearch', label: 'App Search' },
			{ value: 'appInstallMetricsViewContent', label: 'App View Content' },
			{ value: 'appInstallMetricsAppLaunch', label: 'App Launch' },

		],
	},
	{
		value:   'conversions',
		label:   'Conversions',
		metrics: [
			{ value: 'conversionAddToCartAvgValue', label: 'Conversion Add To Cart Avg Value' },
			{ value: 'conversionAddToCartTotalItems', label: 'Conversion Add To Cart Total Items' },
			{ value: 'conversionAddToCartTotalValue', label: 'Conversion Add To Cart Total Value' },
			{ value: 'conversionAddToWishlistAvgValue', label: 'Conversion Add To Wishlist Avg Value' },
			{ value: 'conversionAddToWishlistTotalItems', label: 'Conversion Add To Wishlist Total Items' },
			{ value: 'conversionAddToWishlistTotalValue', label: 'Conversion Add To Wishlist Total Value' },
			{ value: 'conversionLeadTotalValue', label: 'Conversion Lead Total Value' },
			{ value: 'conversionPurchaseAvgValue', label: 'Conversion Purchase Avg Value' },
			{ value: 'conversionPurchaseTotalItems', label: 'Conversion Purchase Total Items' },
			{ value: 'conversionPurchaseTotalValue', label: 'Conversion Purchase Total Value' },
			{ value: 'conversionSignupTotalValue', label: 'Conversion Signup Total Value' },
			{ value: 'conversionRoas', label: 'Conversion ROAS' },
		],
	},
	{
		value:   'video',
		label:   'Video Views',
		metrics: [
			{ value: 'videoWatched25Percent', label: 'Video Watched 25%' },
			{ value: 'videoWatched50Percent', label: 'Video Watched 50%' },
			{ value: 'videoWatched75Percent', label: 'Video Watched 75%' },
			{ value: 'videoWatched95Percent', label: 'Video Watched 95%' },
			{ value: 'videoWatched100Percent', label: 'Video Watched 100%' },
			{ value: 'videoWatched3Seconds', label: 'Video Watched 3s' },
			{ value: 'videoWatched5Seconds', label: 'Video Watched 5s' },
			{ value: 'videoWatched10Seconds', label: 'Video Watched 10s' },
			{ value: 'videoStarted', label: 'Video Started' },
			{ value: 'videoPlaysExpanded', label: 'Video Plays Expanded' },
			{ value: 'videoFullyViewableImpressions', label: 'Video Fully Viewable Impressions' },
			{ value: 'videoViewableImpressions', label: 'Video Viewable Impressions' },
			{ value: 'videoPlaysWithSound', label: 'Video Plays With Sound' },
		],
	},
];

export const mapFormGraphqlErrors = (mutationResponse: Record<string, any>): Record<string, string> => Object.keys(
	mutationResponse?.[0]?.form ?? {},
).reduce((accu, field) => {
	accu[field] = mutationResponse?.[0]?.form?.[field]?.message ?? 'Unknown error';

	return accu;
}, {});
